'use client';

import { Salutation } from '@schuelerhilfe/shared/src/enums/salutation';
import type { BlocksContent } from '@strapi/blocks-react-renderer';
import { useTranslations } from 'next-intl';
import { Button } from '@/components/Button';
import { ContactForm } from '@/components/form/ContactForm';
import { toFieldTextOverrides } from '@/lib/form/fieldTexts';
import type {
  ContactFormSubmitData,
  FormFieldTextEntry,
} from '@/lib/form/types';
import { RichText } from '@/lib/render/RichText';

interface AppointmentContactFormProps {
  onBack: () => void;
  onSubmit: (data: ContactFormSubmitData) => Promise<void>;
  isPending?: boolean;
  backLabel: string;
  ctaLabel: string;
  privacyText?: BlocksContent;
  /** CMS heading override; falls back to the previous hardcoded text. */
  heading?: string;
  /** CMS per-field label/validation-text overrides. */
  formTexts?: FormFieldTextEntry[];
}

export const AppointmentContactForm = ({
  onBack,
  onSubmit,
  isPending,
  backLabel,
  ctaLabel,
  privacyText,
  heading,
  formTexts,
}: AppointmentContactFormProps) => {
  const tForm = useTranslations('form');
  // The SH API Gateway's /api/form/desiredAppointment rejects an empty
  // salutation with a 400, and "Keine Angabe" maps to exactly that, so an
  // appointment offers Herr/Frau only (the contact and voucher forms, whose
  // gateway endpoints accept it, keep the third choice).
  const salutationOptions = [
    { value: Salutation.Mr, label: tForm('mr') },
    { value: Salutation.Mrs, label: tForm('mrs') },
  ];
  return (
    <div>
      <p className="h3 mb-8 text-center">{heading || 'Ihre Kontaktdaten'}</p>

      <ContactForm
        onSubmit={onSubmit}
        salutationOptions={salutationOptions}
        fieldTexts={toFieldTextOverrides(formTexts)}
        extraContent={
          privacyText && privacyText.length > 0 ? (
            <div className="text-muted [&_a]:text-primary mt-4 mb-2 sm:col-span-2">
              {/* The visitor is mid-form: the privacy pages open in a new tab. */}
              <RichText nodes={privacyText} linksInNewTab />
            </div>
          ) : undefined
        }
        className="mx-auto"
      >
        {({ canSubmit, isSubmitting }) => (
          <div className="my-12 flex flex-col justify-center gap-2 md:flex-row md:gap-8">
            <Button
              size="lg"
              variant="secondary"
              type="button"
              onClick={onBack}
              className="w-full text-sm md:w-sm"
            >
              {backLabel}
            </Button>
            <Button
              size="lg"
              type="submit"
              disabled={!canSubmit || isPending}
              className="w-full text-sm md:w-sm"
            >
              {isSubmitting || isPending ? '…' : ctaLabel}
            </Button>
          </div>
        )}
      </ContactForm>
    </div>
  );
};
